import React, { useState, useEffect } from "react";

interface Actor {
  id: string;
  name: string;
  image: string;
  ethnicity?: string;
}

interface BaddieSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectActor: (imageUrl: string) => void;
}

// Map CSV ethnicity values to our filter values
const mapEthnicity = (csvEthnicity: string): string => {
  const mapping: Record<string, string> = {
    "White": "caucasian",
    "Asian": "asian",
    "Ebony": "black",
    "Indian": "indian",
    "Hijabi": "middle-eastern",
    "Latina": "latina",
    "Anime": "anime",
  };
  return mapping[csvEthnicity] || csvEthnicity.toLowerCase();
};

// Anime characters from public/baddies/
const animeCharacters: Actor[] = [
  {
    id: "anime1",
    name: "Megumin",
    image: "/baddies/anime1.jpg",
    ethnicity: "anime",
  },
  {
    id: "anime2",
    name: "Nezuko",
    image: "/baddies/anime2.jpg",
    ethnicity: "anime",
  },
  {
    id: "anime3",
    name: "Mitsuri",
    image: "/baddies/anime3.jpg",
    ethnicity: "anime",
  },
  {
    id: "anime4",
    name: "Power",
    image: "/baddies/anime4.jpg",
    ethnicity: "anime",
  },
];

const BaddieSelectionModal: React.FC<BaddieSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectActor,
}) => {
  const [activeTab, setActiveTab] = useState<"topPicks" | "viewAll">("topPicks");
  const [selectedEthnicity, setSelectedEthnicity] = useState<string>("all");
  const [allCharacters, setAllCharacters] = useState<Actor[]>([]);
  const [topPicksCharacters, setTopPicksCharacters] = useState<Actor[]>([]);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [visibleImages, setVisibleImages] = useState<Set<string>>(new Set());

  // Load characters from CSV on mount
  useEffect(() => {
    const loadCharacters = async () => {
      try {
        const response = await fetch('/new_assets.csv');
        const csvText = await response.text();
        
        // Parse CSV properly (handles commas in quoted fields)
        const parseCSVLine = (line: string): string[] => {
          const result: string[] = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        };

        const lines = csvText.split('\n').filter(line => line.trim());
        const headers = parseCSVLine(lines[0]);
        
        const ethnicityIndex = headers.findIndex(h => h.includes('Ethnicity'));
        const nameIndex = headers.findIndex(h => h === 'Name');
        const urlIndex = headers.findIndex(h => h === 'URL');
        
        const characters: Actor[] = [];
        
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values.length > Math.max(ethnicityIndex, nameIndex, urlIndex)) {
            const ethnicity = mapEthnicity(values[ethnicityIndex]);
            const name = values[nameIndex];
            const imageUrl = values[urlIndex];
            
            if (name && imageUrl) {
              characters.push({
                id: `character_${i}`,
                name: name,
                image: imageUrl,
                ethnicity: ethnicity,
              });
            }
          }
        }
        
        // Add anime characters
        const allChars = [...characters, ...animeCharacters];
        setAllCharacters(allChars);
        
        // Set top picks (first 10)
        setTopPicksCharacters(allChars.slice(0, 10));
      } catch (error) {
        console.error('Error loading characters:', error);
        // Fallback to just anime characters if CSV fails
        setAllCharacters(animeCharacters);
        setTopPicksCharacters(animeCharacters);
      }
    };

    if (isOpen) {
      loadCharacters();
    }
  }, [isOpen]);

  // Preload top picks images immediately
  useEffect(() => {
    if (topPicksCharacters.length > 0) {
      topPicksCharacters.forEach((character) => {
        setVisibleImages((prev) => new Set(prev).add(character.image));
      });
    }
  }, [topPicksCharacters]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!isOpen) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const imgSrc = entry.target.getAttribute("data-src");
            if (imgSrc) {
              setVisibleImages((prev) => new Set(prev).add(imgSrc));
              observer.unobserve(entry.target);
            }
          }
        });
      },
      {
        rootMargin: "200px", // Start loading 200px before entering viewport
        threshold: 0.01,
      }
    );

    // Observe all lazy-load elements
    const lazyElements = document.querySelectorAll("[data-lazy-load]");
    lazyElements.forEach((el) => observer.observe(el));

    return () => {
      lazyElements.forEach((el) => observer.unobserve(el));
    };
  }, [isOpen, activeTab, selectedEthnicity]);

  if (!isOpen) return null;

  const handleActorClick = async (imageUrl: string) => {
    // Convert image to base64
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        onSelectActor(reader.result as string);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error("Failed to convert image to base64:", error);
      // Fallback to URL
      onSelectActor(imageUrl);
    }
    onClose();
  };

  const handleImageLoad = (imageUrl: string) => {
    setLoadedImages((prev) => new Set(prev).add(imageUrl));
  };

  const filteredCharacters = (activeTab === "topPicks" ? topPicksCharacters : allCharacters)
    .filter((character) =>
      selectedEthnicity === "all" ? true : character.ethnicity === selectedEthnicity
    );

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-black border border-white/10 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">
            Select a Model
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Tab Bar and Filters */}
        <div className="p-6 pb-4 border-b border-white/10">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            {/* Tab Bar */}
            <div className="inline-flex rounded-lg bg-white/5 p-1 transition-colors duration-300 border border-white/10">
              <button
                onClick={() => {
                  setActiveTab("topPicks");
                  setSelectedEthnicity("all");
                }}
                className={`px-6 py-2 text-sm font-semibold rounded-md transition-all duration-200 ${
                  activeTab === "topPicks"
                    ? "bg-white text-black shadow-md"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                Top Picks (10)
              </button>
              <button
                onClick={() => setActiveTab("viewAll")}
                className={`px-6 py-2 text-sm font-semibold rounded-md transition-all duration-200 ${
                  activeTab === "viewAll"
                    ? "bg-white text-black shadow-md"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                View All (300+)
              </button>
            </div>

            {/* Filter Section */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-300">
                Ethnicity:
              </span>
              <select
                value={selectedEthnicity}
                onChange={(e) => {
                  setSelectedEthnicity(e.target.value);
                  if (e.target.value !== "all") {
                    setActiveTab("viewAll");
                  }
                }}
                className="px-4 py-2 text-sm font-medium bg-white/5 border border-white/20 rounded-lg text-white hover:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-200"
              >
                <option value="all">All</option>
                <option value="caucasian">White</option>
                <option value="asian">Asian</option>
                <option value="black">Ebony</option>
                <option value="indian">Indian</option>
                <option value="middle-eastern">Hijabi</option>
                <option value="latina">Latina</option>
                <option value="anime">Anime</option>
              </select>
            </div>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredCharacters.map((character) => {
              const isTopPick = activeTab === "topPicks";
              const shouldLoad = isTopPick || visibleImages.has(character.image);
              const isLoaded = loadedImages.has(character.image);
              
              return (
                <button
                  key={character.id}
                  onClick={() => handleActorClick(character.image)}
                  data-lazy-load
                  data-src={character.image}
                  className="group relative aspect-square rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all duration-300 hover:-translate-y-2 hover:scale-105"
                >
                  {shouldLoad && !isLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black pointer-events-none">
                      <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    </div>
                  )}
                  {shouldLoad ? (
                    <img
                      src={character.image}
                      alt={character.name}
                      className={`w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300 ${
                        isLoaded ? "opacity-100" : "opacity-0"
                      }`}
                      onLoad={() => handleImageLoad(character.image)}
                    />
                  ) : (
                    <div className="w-full h-full bg-black"></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <div className="absolute bottom-0 left-0 right-0 p-2 transform translate-y-full group-hover:translate-y-0 transition-transform duration-200">
                    <h3 className="text-white font-semibold text-sm text-center drop-shadow-lg">
                      {character.name}
                    </h3>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaddieSelectionModal;

